import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { useTranslation } from "react-i18next";
import headerImage from "@/assets/headers/header-doacoes.jpg";

const Doacoes = () => {
  const { t } = useTranslation();

  return (
    <Layout>
      <SEO 
        title={t('donations.title')}
        description={t('donations.intro1')}
        keywords="doações terreiro orlando, apoiar umbanda, contribuições terreiro, voluntariado espiritual, materiais rituais umbanda"
        canonicalUrl={`${window.location.origin}/doacoes`}
      />
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${headerImage})` }}
        />
        <div className="absolute inset-0 bg-logo-brown-dark/70" />
        <div className="relative z-10 text-center px-4 container mx-auto">
          <h1 className="font-display text-5xl md:text-7xl text-logo-beige mb-6 font-bold">
            {t('donations.title')}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto font-body">
            {t('donations.subtitle')}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          
          <div className="space-y-4 text-foreground/80 leading-relaxed mb-12">
            <p>{t('donations.intro1')}</p>
            <p>{t('donations.intro2')}</p>
            <p className="font-medium text-sand-dark">{t('donations.intro3')}</p>
          </div>

          <div className="h-px bg-sand-dark/20 my-12" />

          {/* Materiais */}
          <div className="mb-16">
            <h2 className="font-display text-4xl text-sand-dark mb-8 drop-shadow-[0_2px_4px_rgba(28,45,74,0.15)]">
              {t('donations.materials.title')}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h4 className="font-display text-xl text-sand-dark mb-4">{t('donations.materials.ritual.title')}</h4>
                <ul className="space-y-2 text-foreground/80">
                  {(t('donations.materials.ritual.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h4 className="font-display text-xl text-sand-dark mb-4">{t('donations.materials.cleaning.title')}</h4>
                <ul className="space-y-2 text-foreground/80">
                  {(t('donations.materials.cleaning.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h4 className="font-display text-xl text-sand-dark mb-4">{t('donations.materials.food.title')}</h4>
                <ul className="space-y-2 text-foreground/80">
                  {(t('donations.materials.food.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h4 className="font-display text-xl text-sand-dark mb-4">{t('donations.materials.maintenance.title')}</h4>
                <ul className="space-y-2 text-foreground/80">
                  {(t('donations.materials.maintenance.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <p className="text-foreground/70 mt-6 italic">
              *{t('donations.materials.note')}
            </p>
          </div>

          <div className="h-px bg-sand-dark/20 my-12" />

          {/* Formas de Apoiar */}
          <div className="mb-16">
            <h2 className="font-display text-4xl text-sand-dark mb-8 drop-shadow-[0_2px_4px_rgba(28,45,74,0.15)]">
              {t('donations.otherWays.title')}
            </h2>
            
            <div className="space-y-6">
              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h4 className="font-display text-2xl text-sand-dark mb-3">{t('donations.otherWays.volunteer.title')}</h4>
                <p className="text-foreground/80 leading-relaxed">{t('donations.otherWays.volunteer.desc')}</p>
              </div>

              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h4 className="font-display text-2xl text-sand-dark mb-3">{t('donations.otherWays.workParty.title')}</h4>
                <p className="text-foreground/80 leading-relaxed">{t('donations.otherWays.workParty.desc')}</p>
              </div>

              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h4 className="font-display text-2xl text-sand-dark mb-3">{t('donations.otherWays.spread.title')}</h4>
                <p className="text-foreground/80 leading-relaxed">{t('donations.otherWays.spread.desc')}</p>
              </div>
            </div>
          </div>

          <div className="h-px bg-sand-dark/20 my-12" />

          {/* Contribuições Financeiras */}
          <div className="mb-16">
            <h2 className="font-display text-4xl text-sand-dark mb-8 drop-shadow-[0_2px_4px_rgba(28,45,74,0.15)]">
              {t('donations.financial.title')}
            </h2>
            
            <div className="bg-gradient-to-br from-sand-light/30 to-sand-light/10 p-8 rounded-lg border border-sand-dark/20">
              <p className="text-foreground/80 mb-6 leading-relaxed">{t('donations.financial.desc')}</p>

              <div className="grid md:grid-cols-1 gap-6 mt-8 max-w-md mx-auto">
                <div className="bg-background p-6 rounded-lg text-center">
                  <h4 className="font-display text-xl text-sand-dark mb-4">{t('donations.financial.zelle.title')}</h4>
                  <p className="text-foreground/70 mb-2">{t('donations.financial.zelle.label')}</p>
                  <p className="font-mono text-foreground/90 bg-sand-light/30 p-3 rounded text-lg">(407) 775-8479</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-sand-light/30 p-8 rounded-lg text-center">
            <p className="text-foreground/80 leading-relaxed text-lg mb-4">
              "{t('donations.closing.quote')}"
            </p>
            <p className="font-display text-3xl text-sand-dark mt-6">{t('donations.closing.axe')}</p>
          </div>

        </div>
      </section>
    </div>
    </Layout>
  );
};

export default Doacoes;